/**
 * Per-media replay history — why a replay tile shows what it shows.
 *
 * The facts belong to the file, and only the facts of that file: everything written
 * inside it happened between its first and its last frame. What preceded the first
 * frame is folded onto `t = 0` (the state the recording opens on) and what followed
 * the last one is not carried at all — a tile exists only while its file plays, so
 * there is nothing to caption outside its span.
 *
 * The annotations ride the resolve call the player already makes per tile (see
 * `resolvePlayableUrls` in `player.tsx`), so nothing here needs a database date or a
 * join.
 *
 * Nothing here is a diagnosis: the room reports that a track stopped or was switched
 * off, never *why*, so every label below states an observation.
 *
 * These types are declared locally on purpose: the annotations ride on a response the
 * SDK DTOs do not model, so this file plays the same role as `ResolvedContentLocation`
 * in `player.tsx` — a local augmentation until a package release catches up.
 */

/**
 * Vocabulary of the wire contract. It is deliberately smaller and more stable than
 * the backend's own `MeetingSessionMedia` enums: internal enum churn must never reach
 * the client. Any value outside this union is ignored (never rendered, never folded),
 * so the writer can add kinds without a frontend release.
 */
export type ReplayMediaEventKind =
  | "media_added" | "media_removed"
  | "video_on" | "video_off"
  | "audio_on" | "audio_off"
  // The emitter masked its picture at the source: the publish carries on — the mic with
  // it — and black frames take the camera's place. Nothing else moves, which is why this
  // axis exists at all: without it the file shows a black picture over live audio and no
  // other kind can say why.
  | "display_shown" | "display_hidden"
  | "stream_waiting" | "stream_capturing" | "stream_publishing" | "stream_ready"
  | "stream_stopped" | "stream_error"
  | "stream_no_video_track" | "stream_no_audio_track" | "stream_no_tracks";

export interface ReplayMediaEvent {
  /** Milliseconds from the file's first frame — the annotations' own timebase. */
  t: number;
  kind: ReplayMediaEventKind;
}

/**
 * One typed contribution inside the envelope. `kind` and `v` are the only members
 * every entry has; the rest is the form's own. A reader addresses an entry by
 * `(kind, v)` and ignores the kinds it does not know, so a new form reaches the file
 * without a frontend release.
 */
export interface AnnotationEntry {
  /** Dotted, lower-case, e.g. `kalyzee.media-events`. */
  kind: string;
  /** The entry's own schema version. */
  v: number;
  [payload: string]: unknown;
}

/**
 * The annotations envelope as it was written inside the MP4, relayed verbatim by the
 * resolve call. Self-describing on purpose — `timeline.startedAt` anchors the entries'
 * `t` on the wall clock, so the payload still explains itself when the file is
 * downloaded alone.
 */
export interface FileAnnotations {
  /** Envelope version. Only 1 has ever been written. */
  v: number;
  kind: "kalyzee.annotations";
  /** When the writer stamped the file. Provenance only, never read here. */
  stampedAt?: string;
  /**
   * Identity of the recorded media, carrying only the keys the writer had. Never read
   * here — the tile is matched to its recording by VOD id long before this — so it is
   * declared for completeness of the payload, not for use.
   */
  media?: { id?: string; session?: string; meeting?: string; name?: string };
  /** The file's own frame. Absent when the writer could not place the recording. */
  timeline?: {
    /** Wall-clock instant of the file's `t = 0`. */
    startedAt: string;
    durationMs: number;
    /** How the builder placed the recorded segments on the file's timeline. */
    mode: "content" | "wallclock" | "unknown";
  };
  entries: AnnotationEntry[];
}

/**
 * The media history entry of the envelope, the only kind this page reads. Declared as
 * an `AnnotationEntry` narrowed to its own `(kind, v)`, so it is one of the entries the
 * envelope carries rather than a shape of its own.
 */
export interface MediaEventsEntry extends AnnotationEntry {
  kind: "kalyzee.media-events";
  v: 1;
  timebase: "file-ms";
  /** `linear` means no placement table reached the writer: the times are approximate. */
  mapping: "exact" | "linear";
  events: ReplayMediaEvent[];
}

/** What a notice overlay says the tile is doing. Also picks its icon. */
export type TileNoticeKind =
  | "media_removed" | "display_hidden" | "video_off"
  | "no_video_track" | "stream_error" | "stream_stopped";

export interface TileNoticeOverlay {
  kind: TileNoticeKind;
  label: string;
  /**
   * Timeline instant the state was established, when the file dates it. Absent for the
   * state the recording opens on: what established it happened before the first frame,
   * and no instant in the file names it.
   */
  at?: Date;
}

export interface TileEventLogEntry {
  /** On the tile's own timeline, i.e. the file offset placed on the grid's clock. */
  at: Date;
  label: string;
  /** Already happened at the current playhead. */
  past: boolean;
}

export interface TileNotice {
  /** Full-cover explanation. Absent when the tile is simply playing. */
  overlay?: TileNoticeOverlay;
  /**
   * The mic is cut at the playhead. An audio cut never blackens a tile, so it never
   * covers it: the tile reports it as its audio state, drawn exactly like a muted
   * participant in a live meeting. `false` also when the file says nothing.
   */
  audioMuted: boolean;
  /** The tile's whole chronological log, past and future. */
  events: TileEventLogEntry[];
}

type DateLike = Date | string | number;

export interface DeriveTileNoticeInput {
  /** Wall-clock playhead, as the player reports it. `undefined` before the first tick. */
  playhead: Date | undefined;
  /** The player source's span — already on the client clock when one is in use. */
  source: { startedAt: DateLike; endedAt: DateLike };
  /** The recorded file's own annotations, when the resolve carried them. */
  annotations?: FileAnnotations;
}

/**
 * Log wording: one line per transition, observation only.
 *
 * The standing rule is **say what the live tile said**. A viewer watching the replay of
 * a class they attended should read the same words they read at the time, so wherever
 * the live UI already has wording for a fact, that wording wins — « Publication en
 * cours » and not « Diffusion en cours », « Vidéo non publiée » and not « Piste vidéo
 * absente ». It settles every future kind without a taste debate, and it is why the two
 * exceptions below are worth naming: « Flux masqué » / « Flux réaffiché » are the words
 * `settingsSchema` uses for that exact feature, and « Flux interrompu » states an
 * observation where live's « Erreur de publication » names a cause this registry cannot
 * know.
 */
const EVENT_LABELS: Record<ReplayMediaEventKind, string> = {
  media_added: "Source ajoutée",
  media_removed: "Source retirée",
  video_on: "Caméra rétablie",
  video_off: "Caméra désactivée",
  audio_on: "Micro rétabli",
  audio_off: "Micro coupé",
  display_shown: "Flux réaffiché",
  display_hidden: "Flux masqué",
  stream_waiting: "Préparation du flux",
  stream_capturing: "Capture en cours",
  stream_publishing: "Publication en cours",
  stream_ready: "Flux prêt",
  stream_stopped: "Flux arrêté",
  stream_error: "Flux interrompu",
  stream_no_video_track: "Vidéo non publiée",
  stream_no_audio_track: "Audio non publié",
  stream_no_tracks: "Vidéo et audio non publiés",
};

/**
 * The wording for one kind, for a caller outside this module.
 *
 * Exported so the toasts read the registry instead of keeping a second copy of a few
 * of its strings: two copies of one label drift the moment either is edited, and the
 * one that drifts is whichever the person changing the wording did not know about.
 */
export const mediaEventLabel = (kind: ReplayMediaEventKind): string => EVENT_LABELS[kind];

/**
 * Overlay wording: describes the *state* at the playhead, not the transition into it.
 * `video_off` reuses the live tile's own words for the same fact, so a viewer who saw
 * the meeting reads the replay the same way. `display_hidden` names the *image* on
 * purpose: masking replaces the picture and only the picture, the mic keeps flowing —
 * so the audio state stays derived on its own and the tile's mic badge is unaffected.
 */
const OVERLAY_LABELS: Record<TileNoticeKind, string> = {
  media_removed: "Source retirée",
  display_hidden: "Image masquée par l'émetteur",
  video_off: "La vidéo est désactivée",
  no_video_track: "Vidéo non publiée",
  stream_error: "Flux interrompu",
  stream_stopped: "Flux arrêté",
};

const isKnownKind = (kind: string): kind is ReplayMediaEventKind =>
  Object.prototype.hasOwnProperty.call(EVENT_LABELS, kind);

/**
 * A snapshot states a field's value at an instant; it is not a transition into it. The
 * ones carrying a *default* say nothing — and their transition wording would lie
 * ("Caméra rétablie" before anything was ever cut), so they are kept out of the log.
 * Folding them is a no-op by construction, so only the log is affected.
 */
const DEFAULT_SNAPSHOT_KINDS: ReplayMediaEventKind[] = ["video_on", "audio_on", "display_shown", "stream_waiting"];

const toMs = (value: DateLike): number =>
  value instanceof Date ? value.getTime() : new Date(value).getTime();

/**
 * The media history the file carries, or nothing. Checked on `(kind, v)` and on the
 * shape of what it carries: the envelope is relayed verbatim from inside the MP4, so
 * an entry that does not announce itself as this exact form is not one this page can
 * read. Every other entry of the envelope belongs to a consumer that is not this one.
 */
export const selectMediaEvents = (annotations?: FileAnnotations): MediaEventsEntry | undefined => {
  const entries = annotations && Array.isArray(annotations.entries) ? annotations.entries : [];
  // `timebase` is checked too: the same (kind, v) travels from kast.app to storage
  // with wall-clock instants (`at`) before the writer places them (`t`). Only the
  // placed form can be read against the tile's timeline; the other would simply
  // yield no finite `t` — but "no events" is a wrong answer, not a safe one.
  const entry = entries.find((e) =>
    !!e && e.kind === "kalyzee.media-events" && e.v === 1
      && (e as { timebase?: unknown }).timebase === "file-ms"
      && Array.isArray((e as { events?: unknown }).events));
  return entry as MediaEventsEntry | undefined;
};

/** Stream position, as the publisher reports it. `waiting` is the creation default. */
type StreamState =
  | "waiting" | "capturing" | "publishing" | "ready"
  | "stopped" | "error"
  | "no_video_track" | "no_audio_track" | "no_tracks";

/**
 * The state a fold over the events produces. Defaults are the wire contract's:
 * video on, audio on, picture shown, stream waiting, media present — a media that never
 * emitted anything is a media that was fine.
 */
interface FoldState {
  present: boolean;
  video: boolean;
  audio: boolean;
  /** The emitter's picture is the camera's, not a mask. */
  display: boolean;
  stream: StreamState;
  /**
   * Timeline instant each field last changed, so an overlay can say "depuis …", and
   * `undefined` while the value in force is the one the file opens on. The writer folds
   * everything that preceded the first recorded frame onto `t = 0`, so a camera switched
   * off twenty minutes before the recording started and one switched off on the very
   * first frame arrive as the same event: the file dates neither, and the overlay must
   * not pick the opening instant to stand in for a moment it does not know.
   */
  presentAt?: number;
  videoAt?: number;
  displayAt?: number;
  streamAt?: number;
}

const STREAM_STATE_BY_KIND: Partial<Record<ReplayMediaEventKind, StreamState>> = {
  stream_waiting: "waiting",
  stream_capturing: "capturing",
  stream_publishing: "publishing",
  stream_ready: "ready",
  stream_stopped: "stopped",
  stream_error: "error",
  stream_no_video_track: "no_video_track",
  stream_no_audio_track: "no_audio_track",
  stream_no_tracks: "no_tracks",
};

/**
 * Folds one event in. `at` is the instant the resulting state was established, or
 * `undefined` when the event is the file's opening snapshot and therefore undatable —
 * see `FoldState`. Audio needs no instant: its indicator never says "depuis …".
 */
const applyEvent = (state: FoldState, kind: ReplayMediaEventKind, at: number | undefined): void => {
  const stream = STREAM_STATE_BY_KIND[kind];
  if (stream) {
    state.stream = stream;
    state.streamAt = at;
    return;
  }
  switch (kind) {
    case "media_added": state.present = true; state.presentAt = at; break;
    case "media_removed": state.present = false; state.presentAt = at; break;
    case "video_on": state.video = true; state.videoAt = at; break;
    case "video_off": state.video = false; state.videoAt = at; break;
    case "audio_on": state.audio = true; break;
    case "audio_off": state.audio = false; break;
    case "display_shown": state.display = true; state.displayAt = at; break;
    case "display_hidden": state.display = false; state.displayAt = at; break;
    default: break;
  }
};

const buildOverlay = (kind: TileNoticeKind, at?: number): TileNoticeOverlay => ({
  kind,
  label: OVERLAY_LABELS[kind],
  at: at === undefined ? undefined : new Date(at),
});

/**
 * Which folded state explains a black tile, most specific first: a media that is gone
 * outranks a masked picture, which outranks a deliberate cut, which outranks a missing
 * track, which outranks whatever the publisher last reported. Masking sits above the
 * camera switch because it replaces the image at the source whatever that switch is
 * doing, and because it is what a live participant actually saw. A healthy pipeline
 * position (waiting/capturing/publishing/ready) explains nothing and produces no
 * overlay — the tile is just playing.
 */
const overlayFromState = (state: FoldState): TileNoticeOverlay | undefined => {
  if (!state.present) return buildOverlay("media_removed", state.presentAt);
  if (!state.display) return buildOverlay("display_hidden", state.displayAt);
  if (!state.video) return buildOverlay("video_off", state.videoAt);
  if (state.stream === "no_video_track" || state.stream === "no_tracks") return buildOverlay("no_video_track", state.streamAt);
  if (state.stream === "error") return buildOverlay("stream_error", state.streamAt);
  if (state.stream === "stopped") return buildOverlay("stream_stopped", state.streamAt);
  return undefined;
};

/**
 * What to tell the viewer about one replay tile at the current playhead.
 *
 * Pure: same inputs, same output — the caller decides how often to ask.
 *
 * Event times are file offsets, so they land on the tile's own timeline by adding
 * `source.startedAt` — which is where the grid placed the file, whether that came from
 * the record dates or from the publishing page's clock (`extra.clientPublishStartedAt`,
 * see `player.tsx`). No wall-clock arithmetic is needed and none is done.
 *
 * Outside the file's own span there is nothing to say: the tile is not displayed there,
 * and the file annotates nothing before its first frame or after its last. Inside it,
 * the folded media state — dated only where the file dates it, so a state the recording
 * already opened on is stated without an instant rather than pinned on the first frame.
 * The audio state is independent of it: a cut mic is never a reason to cover the
 * picture. The log is built either way — the dev pane lists a tile's whole history
 * whatever the playhead is doing.
 */
export const deriveTileNotice = (input: DeriveTileNoticeInput): TileNotice => {
  const { playhead, source, annotations } = input;
  const startedAtMs = toMs(source.startedAt);
  const endedAtMs = toMs(source.endedAt);
  // An unreadable playhead is no playhead: claiming a state from it would fold the
  // whole history in one go (every comparison against NaN is false).
  const playheadMs = playhead && Number.isFinite(playhead.getTime()) ? playhead.getTime() : undefined;

  // Unknown kinds and unreadable offsets are dropped rather than guessed at, then
  // sorted defensively: the contract says ascending, the fold depends on it. The list
  // itself is type-checked too: the payload is relayed verbatim from inside the file,
  // so nothing on the way here guarantees its shape, and this runs on every playhead
  // pass — a throw would take the whole replay page down.
  const entry = selectMediaEvents(annotations);
  const rawEvents = entry && Array.isArray(entry.events) ? entry.events : [];
  // `opening` marks the events sitting on the file's first frame: the writer folds the
  // whole history that preceded the recording onto `t = 0`, so those state what the file
  // opens on without saying when it became true. They are kept — the state is a fact —
  // but they carry no instant. (A negative offset no writer emits is read the same way:
  // whatever it describes is older than the file.)
  const events = rawEvents
    .filter((e) => !!e && isKnownKind(e.kind as string) && Number.isFinite(e.t))
    .map((e) => ({ at: startedAtMs + e.t, kind: e.kind, opening: e.t <= 0 }))
    .filter((e) => Number.isFinite(e.at))
    .sort((a, b) => a.at - b.at);

  // Two kinds of snapshot. The writer folds whatever preceded the first recorded frame
  // onto `t = 0`, so the file's own first instant is a snapshot whether or not a
  // `media_added` sits there — a media created long before the recording started emits
  // none. A creation later in the file announces itself with `media_added`.
  const snapshotInstants = new Set(events.filter((e) => e.kind === "media_added").map((e) => e.at));
  snapshotInstants.add(startedAtMs);
  const log: TileEventLogEntry[] = events
    .filter((e) => !(snapshotInstants.has(e.at) && DEFAULT_SNAPSHOT_KINDS.includes(e.kind)))
    .map((e) => ({
      at: new Date(e.at),
      label: EVENT_LABELS[e.kind],
      past: playheadMs !== undefined && e.at <= playheadMs,
    }));

  if (playheadMs === undefined) return { audioMuted: false, events: log };
  // Outside the file's span the grid shows no tile, so there is no frame to caption —
  // and the file itself makes no claim there.
  if (playheadMs < startedAtMs || playheadMs > endedAtMs) return { audioMuted: false, events: log };

  const state: FoldState = { present: true, video: true, audio: true, display: true, stream: "waiting" };
  events.forEach((e) => {
    if (e.at > playheadMs) return;
    applyEvent(state, e.kind, e.opening ? undefined : e.at);
  });

  return {
    overlay: overlayFromState(state),
    audioMuted: !state.audio || state.stream === "no_audio_track" || state.stream === "no_tracks",
    events: log,
  };
};
